import React, { useContext, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import SimpleDateInput from '../../common/DateInput/SimpleDateInput';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import {
  ActivateDealerQuoteDto,
  ActivateResponse,
  SelectOpt
} from '../../common/CommonTypes';
import TextInput from '../../common/TextInput/TextInput';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import Checkbox from '../../common/Checkbox/Checkbox';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import { SiteContext } from '../../../context/SiteContext';

type DealerQuoteDialogProps = {
  showDialog: boolean;
  onCloseModal: () => void;
  onContinueModal: () => void;
  includeExcessReturn: boolean;
  includeDriveaway: boolean;
  quoteId: string;
  brand: string;
  vrn: string;
};
type DealerQuoteDialogErrors = {
  userVrn: string;
  startDate: string;
  startTime: string;
  iAgree: string;
};
const initialFormErrors: DealerQuoteDialogErrors = {
  userVrn: '',
  startDate: '',
  startTime: '',
  iAgree: ''
};

const activateDealerQuoteDto: ActivateDealerQuoteDto = {
  quoteId: '',
  startDate: '',
  startTime: '',
  vrn: '',
  dealerRepresentative: '',
  excessProtectActive: false,
  coverNoteActive: false
};

const DealerQuoteDialog = ({
  showDialog,
  onCloseModal,
  brand,
  includeDriveaway,
  includeExcessReturn,
  quoteId,
  vrn
}: DealerQuoteDialogProps): JSX.Element => {
  const [ShowActiveQuoteSection, setShowActiveQuoteSection] = useState(false);
  const [driveAway, setDriveAway] = useState(false);
  const [excessReturn, setExcessReturn] = useState(false);
  const [startDate, setstartDate] = useState<Date | undefined>();
  const [startTime, setstartTime] = useState('');
  const [userVrn, setUserVrn] = useState(vrn);
  const [confirmationTextMessage, setConfirmationTextMessage] = useState(false);
  const [startTimeOptions, setStartTimeOptions] = useState<SelectOpt[]>([]);
  const [formErrors, setFormErrors] =
    useState<DealerQuoteDialogErrors>(initialFormErrors);
  const { site } = useContext(SiteContext);
  const today = new Date();
  const maxStartDate = new Date();
  maxStartDate.setDate(maxStartDate.getDate() + 22);

  useEffect(() => {
    startDate ? onChangeStartTime('') : onChangeStartTime(startTime);
    const date =
      typeof startDate === 'string' ? new Date(startDate) : startDate;
    if (date?.toDateString() === new Date().toDateString())
      getStartTimeOptions(new Date().getHours() + 1);
    else getStartTimeOptions();
  }, [startDate]);

  const onClickSubmit = async () => {
    if (validateDealerQuote()) {
      activateDealerQuoteDto.startTime = startTime;
      activateDealerQuoteDto.vrn = userVrn;
      activateDealerQuoteDto.startDate = startDateValue();
      activateDealerQuoteDto.coverNoteActive = driveAway;
      activateDealerQuoteDto.excessProtectActive = driveAway || excessReturn;

      activateDealerQuoteDto.dealerRepresentative =
        site.user.insuranceDealerRepresentative ?? '';
      activateDealerQuoteDto.quoteId = quoteId;
      ActivateDealerQuote(
        setDealerQuoteActivateSuccess,
        setDealerQuoteActivateFailure
      );
    }
  };
  const startDateValue = (): string => {
    if (startDate !== undefined) {
      const day = startDate.getDate();
      const month = startDate.getMonth() + 1; // Months are zero-based
      const year = startDate.getFullYear();

      const formattedDay = day < 10 ? `0${day}` : day.toString();
      const formattedMonth = month < 10 ? `0${month}` : month.toString();

      return `${year}-${formattedMonth}-${formattedDay}`;
    } else {
      return '';
    }
  };

  const setDealerQuoteActivateSuccess = (
    activateResponse: ActivateResponse
  ) => {
    console.log(activateResponse.activateStatus);
    ResetValues();
  };

  const setDealerQuoteActivateFailure = (errorMessage: string) => {
    console.log(errorMessage);
    ResetValues();
  };

  const ResetValues = () => {
    const initialErrors = { ...initialFormErrors };
    setFormErrors(initialErrors);
    setstartDate(undefined);
    setstartTime('');
    setConfirmationTextMessage(false);
    setShowActiveQuoteSection(false);
    onCloseModal();
  };

  const ActivateDealerQuote = async (
    successFunction: (activateStatus: ActivateResponse) => void,
    failureFunction: (errorMessage: string) => void
  ) => {
    const controller = new AbortController();

    await new CarInsuranceApi().ActivateDealerQuoteAsync(
      activateDealerQuoteDto,
      site.user.token,
      successFunction,
      failureFunction,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onClickClose = () => {
    ResetValues();
  };

  const onChangeStartTime = (value: string) => {
    setstartTime(value);
  };

  const onChangeUserVrn = (value: string) => {
    setUserVrn(value);
  };

  const handleOnChangeConfirmationText = () => {
    setConfirmationTextMessage(!confirmationTextMessage);
  };

  const validateDealerQuote = (): boolean => {
    let isValid = true;
    const updatedFormErrors = { ...initialFormErrors };
    if (!confirmationTextMessage) {
      updatedFormErrors.iAgree = 'Confirmation is required';
      isValid = false;
    }
    if (CommonValidation.isEmpty(startDate)) {
      isValid = false;
      updatedFormErrors.startDate = 'Start date is required';
    } else if (!CommonValidation.isValidDate(startDate, false)) {
      isValid = false;
      updatedFormErrors.startDate = 'Start date is invalid';
    }
    if (!SelectInputHelper.isValidValue(startTime)) {
      isValid = false;
      updatedFormErrors.startTime = 'Start time is required';
    }
    if (
      SelectInputHelper.isNullValue(userVrn) ||
      SelectInputHelper.isEmptyValue(userVrn)
    ) {
      updatedFormErrors.userVrn = 'Vehicle registration is required';
      isValid = false;
    }

    setFormErrors(updatedFormErrors);

    return isValid;
  };

  const onDriveawaySelect = () => {
    setShowActiveQuoteSection(true);
    setDriveAway(true);
    setExcessReturn(false);
    setUserVrn(vrn);
  };

  const onExcessVoucheSelect = () => {
    setShowActiveQuoteSection(true);
    setExcessReturn(true);
    setDriveAway(false);
    setUserVrn(vrn);
  };

  const onChangeStartDateValue = (date: Date | null): void => {
    if (date !== null) setstartDate(date);
  };
  const getStartTimeOptions = (startFrom?: number) => {
    const options = [];

    for (let i = startFrom ?? 0; i <= 23; i++) {
      const timeValue = i < 10 ? '0' + i + ':00' : i + ':00';
      const item: SelectOpt = {
        id: 'T' + i,
        value: timeValue,
        text: timeValue,
        disabled: false
      };
      options.push(item);
    }

    setStartTimeOptions(options);
  };

  const modalClass = 'modal-dialog-' + brand;
  const headertext = 'Activate';
  return (
    <Modal
      role={modalClass}
      className={`insurance-${brand}`}
      isOpen={showDialog}
      toggle={onClickClose}>
      <ModalHeader toggle={onClickClose}>
        {ShowActiveQuoteSection && headertext}
      </ModalHeader>
      <ModalBody>
        {!ShowActiveQuoteSection && (
          <div>
            {includeExcessReturn && (
              <>
                <p>£250 Excess Voucher only</p>
                <br />
                <p>
                  By proceeding, I confirm that the customer has confirmed that
                  they do not wish to receive Free Driveaway Insurance and have
                  arranged motor insurance separately to drive their car home
                  following vehicle handover.
                </p>
                <br />
                <ButtonContinue
                  name='btn-excessVoucher'
                  text='Select'
                  onClick={onExcessVoucheSelect}
                  additionalClassNames='btn-ok'
                />
              </>
            )}
            {includeExcessReturn && includeDriveaway && (
              <>
                <br />
                <br />
                <p>Or</p>
                <br />
              </>
            )}
            {includeDriveaway && (
              <>
                <p>Activate Free Driveaway and £250 Excess Voucher </p>
                <br />
                <p>
                  By proceeding, I confirm that the applicant(s) wishes to
                  receive Free Driveaway Insurance to drive their car home
                  following vehicle handover.
                </p>
                <br />
                <ButtonContinue
                  name='btn-selectdriveway'
                  text='Select'
                  onClick={onDriveawaySelect}
                  additionalClassNames='btn-ok'
                />
              </>
            )}
          </div>
        )}
        {ShowActiveQuoteSection && (
          <div>
            <div className='one-col'>
              <div className='control-label'>Set start date</div>
              <div className='control'>
                <SimpleDateInput
                  name='quote-start-date'
                  value={startDate}
                  error={formErrors.startDate}
                  onDateChange={onChangeStartDateValue}
                  minimumDate={today}
                  maximumDate={maxStartDate}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Set start time</div>
              <div className='control'>
                <SelectInput
                  name='starttime'
                  value={startTime}
                  options={startTimeOptions}
                  onChange={onChangeStartTime}
                  forceDefault
                  addDefaultOption
                  error={formErrors.startTime}
                />
              </div>
            </div>
            <div className='one-col'>
              <div className='control-label'>Vehicle registration</div>
              <div className='control flex-column'>
                <TextInput
                  placeholder='Registration number'
                  name='registration-number'
                  className='input-text w250'
                  value={userVrn}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onChangeUserVrn(e.currentTarget.value)
                  }
                  error={formErrors.userVrn}
                  readOnly={false}
                />
              </div>
            </div>
            <div>
              <Checkbox
                id='chk-confiramtion-text'
                checked={confirmationTextMessage}
                text="I Confirm that I have evidenced the applicant's Proof of Identity and Home address"
                onChange={() => {
                  handleOnChangeConfirmationText();
                }}
              />
              {formErrors.iAgree && (
                <div className='error-style'>{formErrors.iAgree}</div>
              )}
            </div>
            <ButtonContinue
              name='btn-submit'
              text='Submit'
              onClick={() => {
                onClickSubmit();
              }}
              additionalClassNames='btn-save'
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default DealerQuoteDialog;
